import { createContext, useContext, useState, useCallback, useEffect } from "react";
import translations from "./translations";

const LS_KEY = "tgaone_lang";
const SUPPORTED = ["de", "en", "sk"];
const API = "/api";

const LanguageContext = createContext({ lang: "de", t: (key) => key, setLanguage: () => {} });

export function useTranslation() {
  return useContext(LanguageContext);
}

/**
 * Interpoliert {name}-Platzhalter in einem String. Werte werden zu String konvertiert.
 * Beispiel: interpolate("Hallo {name}", { name: "Ronny" }) -> "Hallo Ronny"
 */
function interpolate(str, vars) {
  if (!vars || typeof str !== "string") return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? String(vars[k]) : m));
}

function pickInitialLang(userLanguage) {
  // 1. lokaler Override (User hat in der App bewusst gewechselt) hat Vorrang
  try {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(LS_KEY) : null;
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch {}
  // 2. User-Default vom Server
  if (userLanguage && SUPPORTED.includes(userLanguage)) return userLanguage;
  return "de";
}

export function LanguageProvider({ children, userLanguage }) {
  const [lang, setLang] = useState(() => pickInitialLang(userLanguage));

  // Wenn der User wechselt (Login), Server-Sprache anwenden — sofern kein lokaler Override existiert
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(LS_KEY);
      if (stored && SUPPORTED.includes(stored)) return; // lokaler Override beibehalten
    } catch {}
    if (userLanguage && SUPPORTED.includes(userLanguage)) setLang(userLanguage);
  }, [userLanguage]);

  // lang-Attribut auf <html> spiegeln (a11y + Browser-Hinweis)
  useEffect(() => {
    if (typeof document !== "undefined" && document.documentElement) {
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);

  const setLanguage = useCallback(async (newLang) => {
    if (!SUPPORTED.includes(newLang)) return;
    setLang(newLang);
    try { window.localStorage.setItem(LS_KEY, newLang); } catch {}
    // Im Hintergrund: am User-Profil persistieren (Best-Effort, kein Block bei Fehler)
    try {
      const token = window.localStorage.getItem("token");
      if (token) {
        await fetch(`${API}/users/me/language`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ language: newLang }),
        });
      }
    } catch {
      // Ignorieren — lokal ist gesetzt, beim nächsten Login holen wir Server-Wert
    }
  }, []);

  const t = useCallback(
    (key, varsOrFallback, fallback) => {
      // Signatur-Kompatibilität: t(key) | t(key, fallback) | t(key, vars) | t(key, vars, fallback)
      let vars = null;
      let fb = fallback;
      if (typeof varsOrFallback === "string") fb = varsOrFallback;
      else if (varsOrFallback && typeof varsOrFallback === "object") vars = varsOrFallback;

      const entry = translations[key];
      if (!entry) return interpolate(fb || key, vars);
      const raw = entry[lang] || entry["de"] || fb || key;
      return interpolate(raw, vars);
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, t, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}
